import { useEffect, useMemo, useRef, useState, isValidElement, type ReactNode } from "react";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import { IcBackTo, IcDownload, IcCancel } from "@racoon/icons";
import { downloadBlob } from "@racoon/lib";
import { Search, ChevronRight, Filter as FilterIcon, Plus } from "lucide-react";
import {
  DateRangePicker,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useMediaQuery,
  useToast,
} from "@racoon/ui";
import { useVirtualizer } from "@tanstack/react-virtual";
import * as XLSX from "xlsx";

import { Namespace, useT } from "@racoon/i18n";

import {
  useGetGovGeoAccessQuery,
  usePickupLocationsQuery,
  useContainerTypesQuery,
  useJobsQuery,
  useServiceProvidersQuery,
  useTrucksQuery,
  useDepotsQuery,
  useDisposalSitesQuery,
  useSuspiciousLocationsQuery,
  useAnomaliesListQuery,
  useLazyExportJobsReportQuery,
  useLazyExportPickupLocationsReportQuery,
  useLazyGetGovDisposalVisitsQuery,
  useListAiVerificationsQuery,
} from "~/shared/api/govDash/govDash.api";
import { FilterForm, type ExtraFilterItem } from "~/features/filter/filter.form";
import {
  getRecordsListFilterConfig,
  hasRecordsListFilters,
  type RecordTypeRoute,
} from "../lib/records-list-filter-config";
import { applyGovTrucksExtraFilters, deriveGovTrucksFilterOptions } from "../lib/gov-trucks-filter";
import {
  getRecordsConfig,
  type RecordsConfigItem,
  type WasteExportTab,
} from "../lib/records.config";
import { useAccumulatedList } from "../lib/use-accumulated-list";
import { toDateParam } from "~/shared/utils/filterDateFormatter";
import { CardContentWrapper } from "~/ui/components/cardContent.wrapper";
import { InfoRow } from "~/ui/components/infoRow";
import { useCanEditGovDash } from "~/shared/hooks/useCanEditGovDash";
import { SuspiciousLocationForm } from "../components/suspicious-location-form";
import { SuspiciousSitesDetails as SuspiciousSitesDetailsComponent } from "../components/suspicious-sites.details";
import { AnomaliesDetails } from "../components/anomalies.details";
import { WasteExportCardPhotos } from "../components/waste-export-card-photos";
import { WasteExportShareButton } from "../components/waste-export-share.button";
import { AiVerdictBadges } from "../lib/ai-verification.badges";
import { aiVerdictScoreFilters } from "../lib/ai-verification.verdicts";
import { RecordsMapView } from "../components/records-map.view";
import { TrucksTable } from "../components/trucks.table";
import { ConnectProto } from "@racoon/api";

const RECORD_TYPE_TO_SUBTITLE_KEY: Record<RecordTypeRoute, string> = {
  "service-providers": "subtitle.serviceProvider",
  "waste-export": "wasteExport.task",
  "pickup-locations": "subtitle.pickupLocation",
  trucks: "subtitle.truck",
  depots: "subtitle.depot",
  "disposal-sites": "subtitle.disposalSite",
  "suspicious-sites": "subtitle.suspiciousSite",
  anomalies: "anomalies.anomaly",
};

// Which waste-export tab is open lives in the URL (`?tab=ai`) rather than in state, so
// returning from a record detail, a reload and the browser's back button all land on the
// tab the user was on.
const WASTE_EXPORT_TAB_PARAM = "tab";

// Row/total selectors for useAccumulatedList. Declared at module level so their identity
// is stable across renders.
const selectJobRows = (data: any) => data?.jobs;
const selectAiVerificationRows = (data: any) => data?.aiVerifications;
const selectListTotal = (data: any) => data?.listInfo?.totalItems;

// Sentinel value for the disposal-site select in the visits report sheet. Radix's
// Select reserves the empty string, so "all" needs its own non-empty value.
const ALL_DISPOSAL_SITES = "__all__";

// Selected options of an id-valued filter row (district, MVO) as bigint ids; values
// that do not parse are skipped.
const selectedFilterIds = (filters: ExtraFilterItem[], key: string): bigint[] =>
  (filters.find((f) => f.key === key)?.selectedOptions ?? []).reduce<bigint[]>((acc, v) => {
    try {
      acc.push(BigInt(v));
    } catch {
      /* ignore invalid id */
    }
    return acc;
  }, []);

// Coerce the various shapes `content` can take after passing through the store's
// normalizeBigInt into a real Uint8Array: a genuine Uint8Array/ArrayBuffer, a base64
// string, a plain array of byte values, or a normalized object of numeric keys
// ({ "0": 80, "1": 75, ... }). Returns null if it can't recover any bytes.
const toUint8Array = (content: unknown): Uint8Array | null => {
  if (!content) return null;
  if (content instanceof Uint8Array) return content;
  if (content instanceof ArrayBuffer) return new Uint8Array(content);
  if (typeof content === "string") {
    try {
      const binary = atob(content);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      return bytes;
    } catch {
      return null;
    }
  }
  if (Array.isArray(content)) return Uint8Array.from(content as number[]);
  if (typeof content === "object") {
    const values = Object.values(content as Record<string, unknown>);
    if (values.length && values.every((v) => typeof v === "number")) {
      return Uint8Array.from(values as number[]);
    }
  }
  return null;
};

export const RecordsPage = () => {
  const t = useT();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { recordType, selectedRecordId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // "jobs" = the waste-export jobs registry, "ai" = the photo-recognition (AI
  // verification) list. Only the waste-export record type has two tabs.
  const wasteExportTab: WasteExportTab =
    searchParams.get(WASTE_EXPORT_TAB_PARAM) === "ai" ? "ai" : "jobs";
  const isAiVerificationList = recordType === "waste-export" && wasteExportTab === "ai";
  /** Keeps `?tab=ai` on links out of the list so the tab survives a drill-in. */
  const wasteExportTabSearch = wasteExportTab === "ai" ? `?${WASTE_EXPORT_TAB_PARAM}=ai` : "";

  const selectWasteExportTab = (tab: WasteExportTab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === "ai") next.set(WASTE_EXPORT_TAB_PARAM, "ai");
    else next.delete(WASTE_EXPORT_TAB_PARAM);
    setSearchParams(next, { replace: true });
  };

  const [isAddingLocation, setIsAddingLocation] = useState(false);
  const canEditGovDash = useCanEditGovDash();
  const [anomaliesStartDate, setAnomaliesStartDate] = useState<Date | undefined>(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d;
  });
  const [anomaliesEndDate, setAnomaliesEndDate] = useState<Date | undefined>(new Date());
  // Waste-export jobs use their own date range, defaulting to "today" (start of
  // today .. end of today) instead of the anomalies' one-month-back default.
  const [jobsStartDate, setJobsStartDate] = useState<Date | undefined>(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [jobsEndDate, setJobsEndDate] = useState<Date | undefined>(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  });
  const [isReportSheetOpen, setIsReportSheetOpen] = useState(false);
  const [reportStartDate, setReportStartDate] = useState<Date | undefined>(jobsStartDate);
  const [reportEndDate, setReportEndDate] = useState<Date | undefined>(jobsEndDate);
  // Disposal-site filter for the visits report. ALL_DISPOSAL_SITES means "don't
  // filter"; any other value is a disposal site name matched against
  // visit.disposalSiteName.
  const [reportDisposalSite, setReportDisposalSite] = useState<string>(ALL_DISPOSAL_SITES);
  const [appliedExtraFilters, setAppliedExtraFilters] = useState<ExtraFilterItem[]>([]);
  const [viewMode, setViewMode] = useState<"list" | "map">("list");
  const { data: govGeoAccessData } = useGetGovGeoAccessQuery({});
  const districtOrganizationIds = useMemo(
    () => (govGeoAccessData?.govGeoAccesses ?? []).map((a) => a.organizationId),
    [govGeoAccessData],
  );
  const [searchTerm, setSearchTerm] = useState("");
  // Normalized search value sent to the backend. It is part of the jobs filter
  // state, so it feeds both the request and jobsFilterKey (which resets the
  // accumulated pages + pagination when any filter changes).
  const jobsSearch = searchTerm.trim();

  // The "district" row narrows the government scope to the picked districts. With
  // nothing picked every geo-access district is sent: an empty
  // district_organization_ids would make the backend fall back to the caller's own
  // organization instead.
  const listDistrictOrganizationIds = useMemo(() => {
    const recordTypesWithDistrictFilter: RecordTypeRoute[] = [
      "service-providers",
      "pickup-locations",
      "waste-export",
    ];
    if (!recordTypesWithDistrictFilter.includes(recordType as RecordTypeRoute)) {
      return districtOrganizationIds;
    }
    const selected = selectedFilterIds(appliedExtraFilters, "district");
    return selected.length > 0 ? selected : districtOrganizationIds;
  }, [districtOrganizationIds, recordType, appliedExtraFilters]);

  const govOrganizationQueryArg = useMemo(() => {
    const recordTypesWithMvoFilter: RecordTypeRoute[] = [
      "pickup-locations",
      "depots",
      "waste-export",
    ];
    const applyMvoFilter =
      recordType !== undefined && recordTypesWithMvoFilter.includes(recordType as RecordTypeRoute);
    const serviceProviderIds: bigint[] = applyMvoFilter
      ? selectedFilterIds(appliedExtraFilters, "mvo")
      : [];
    return {
      districtOrganizationIds: listDistrictOrganizationIds,
      serviceProviderIds,
    };
  }, [listDistrictOrganizationIds, recordType, appliedExtraFilters]);

  // ServiceProviders also feeds the MVO filter options, which stay scoped to every
  // district: narrowing them by the district pick would drop an already selected MVO
  // out of `options`, and the sync effect below then clears the selection. Only the
  // service-providers registry itself lists by the picked districts.
  const { data: serviceProvidersData } = useServiceProvidersQuery({
    districtOrganizationIds:
      recordType === "service-providers" ? listDistrictOrganizationIds : districtOrganizationIds,
  });
  // Container-type options are scoped to the districts only, not to the MVO filter:
  // narrowing them alongside `mvo` would drop an already selected container type out
  // of `options`, and the sync effect below then clears the selection.
  const { data: containerTypesData } = useContainerTypesQuery(
    { districtOrganizationIds },
    {
      skip:
        (recordType as RecordTypeRoute) !== "pickup-locations" ||
        districtOrganizationIds.length === 0,
    },
  );

  // The filter offers one option per container-type *name*, since every MVO owns its
  // own copy of the same nominal type ("1.1 m3", "8 m3 bunker"). An option's value is
  // that name, and this map expands it back into every MVO's matching type id, so one
  // pick filters on all of their variants at once.
  const containerTypeIdsByName = useMemo(() => {
    const byName = new Map<string, bigint[]>();
    for (const type of containerTypesData?.containerTypes ?? []) {
      const name = type.name?.trim();
      if (!name) continue;
      const ids = byName.get(name);
      if (ids) ids.push(type.id);
      else byName.set(name, [type.id]);
    }
    return byName;
  }, [containerTypesData]);

  const containerTypeIds = useMemo<bigint[]>(() => {
    if ((recordType as RecordTypeRoute) !== "pickup-locations") return [];
    const selected =
      appliedExtraFilters.find((f) => f.key === "containerType")?.selectedOptions ?? [];
    const ids = selected.flatMap((name) => containerTypeIdsByName.get(name) ?? []);
    return Array.from(new Set(ids)).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  }, [recordType, appliedExtraFilters, containerTypeIdsByName]);

  const pickupLocationsArg = useMemo(
    () => ({ filter: govOrganizationQueryArg, containerTypeIds }),
    [govOrganizationQueryArg, containerTypeIds],
  );

  const { data: pickupLocationsData } = usePickupLocationsQuery(pickupLocationsArg);

  const JOBS_PAGE_SIZE = 20;

  // Both waste-export tabs (jobs and AI photo recognition) share this date range.
  const jobsTimeFilter = useMemo(
    () => ({
      from: jobsStartDate
        ? { seconds: BigInt(Math.floor(jobsStartDate.getTime() / 1000)) }
        : undefined,
      to: jobsEndDate ? { seconds: BigInt(Math.floor(jobsEndDate.getTime() / 1000)) } : undefined,
    }),
    [jobsStartDate, jobsEndDate],
  );

  const jobStatusesFilter = useMemo<ConnectProto.JobStatus[]>(() => {
    const selected = appliedExtraFilters.find((f) => f.key === "jobStatus")?.selectedOptions ?? [];
    return selected.reduce<ConnectProto.JobStatus[]>((acc, v) => {
      const parsed = Number(v);
      if (Number.isInteger(parsed) && parsed in ConnectProto.JobStatus) {
        acc.push(parsed as ConnectProto.JobStatus);
      }
      return acc;
    }, []);
  }, [appliedExtraFilters]);

  const jobsFilterKey = useMemo(
    () =>
      [
        govOrganizationQueryArg.districtOrganizationIds.map(String).join(","),
        govOrganizationQueryArg.serviceProviderIds.map(String).join(","),
        jobStatusesFilter.join(","),
        String(jobsTimeFilter.from?.seconds ?? ""),
        String(jobsTimeFilter.to?.seconds ?? ""),
        jobsSearch,
      ].join("|"),
    [govOrganizationQueryArg, jobStatusesFilter, jobsTimeFilter, jobsSearch],
  );

  const jobsArg = useMemo(
    () => ({
      ...govOrganizationQueryArg,
      jobStatuses: jobStatusesFilter,
      timeFilter: jobsTimeFilter,
      search: jobsSearch || undefined,
    }),
    [govOrganizationQueryArg, jobStatusesFilter, jobsTimeFilter, jobsSearch],
  );

  const jobsList = useAccumulatedList({
    useListQuery: useJobsQuery,
    arg: jobsArg,
    pageSize: JOBS_PAGE_SIZE,
    filterKey: jobsFilterKey,
    selectRows: selectJobRows,
    selectTotal: selectListTotal,
    skip: wasteExportTab !== "jobs",
  });

  // The AI verification list carries the verified job inside every row, so its cards
  // read like the jobs cards. It has no `search` field, hence no jobsSearch here (the
  // search input is hidden on that tab) and none in its filter key.
  const aiVerdictFields = useMemo(
    () => appliedExtraFilters.find((f) => f.key === "aiVerdict")?.selectedOptions ?? [],
    [appliedExtraFilters],
  );

  const aiVerificationsArg = useMemo(
    () => ({
      ...govOrganizationQueryArg,
      timeFilter: jobsTimeFilter,
      scoreFilters: aiVerdictScoreFilters(aiVerdictFields),
    }),
    [govOrganizationQueryArg, jobsTimeFilter, aiVerdictFields],
  );

  const aiVerificationsFilterKey = useMemo(
    () =>
      [
        govOrganizationQueryArg.districtOrganizationIds.map(String).join(","),
        govOrganizationQueryArg.serviceProviderIds.map(String).join(","),
        String(jobsTimeFilter.from?.seconds ?? ""),
        String(jobsTimeFilter.to?.seconds ?? ""),
        aiVerdictFields.join(","),
      ].join("|"),
    [govOrganizationQueryArg, jobsTimeFilter, aiVerdictFields],
  );

  const aiVerificationsList = useAccumulatedList({
    useListQuery: useListAiVerificationsQuery,
    arg: aiVerificationsArg,
    pageSize: JOBS_PAGE_SIZE,
    filterKey: aiVerificationsFilterKey,
    selectRows: selectAiVerificationRows,
    selectTotal: selectListTotal,
    skip: wasteExportTab !== "ai",
  });

  const activeWasteExportList = wasteExportTab === "ai" ? aiVerificationsList : jobsList;
  const { loadNextPage: loadNextWasteExportPage } = activeWasteExportList;
  const loadedWasteExportCount = activeWasteExportList.rows.length;

  const jobsData = useMemo(
    () => ({ jobs: jobsList.rows, listInfo: { totalItems: jobsList.totalItems } }),
    [jobsList.rows, jobsList.totalItems],
  );

  const aiVerificationsData = useMemo(
    () => ({
      aiVerifications: aiVerificationsList.rows,
      listInfo: { totalItems: aiVerificationsList.totalItems },
    }),
    [aiVerificationsList.rows, aiVerificationsList.totalItems],
  );

  // Download the waste-export jobs registry as an xlsx file. We reuse the same
  // org/filter/date args as the jobs list (minus pagination — the report covers
  // the whole filtered set), then turn the returned bytes into a downloadable
  // Blob. Uses the lazy query so the request fires on click, not on render.
  const [triggerExportJobsReport, { isFetching: isExportingJobsReport }] =
    useLazyExportJobsReportQuery();

  // Export the jobs registry over the report sheet's reporting period. The status
  // filter distinguishes the two reports: NOT_COMPLETED for the "schedule failure"
  // report, an empty list for the full export.
  const handleExportJobsReport = async (jobStatuses: ConnectProto.JobStatus[]) => {
    if (isExportingJobsReport) return;
    const { data: report } = await triggerExportJobsReport({
      ...govOrganizationQueryArg,
      timeFilter: {
        from: reportStartDate
          ? { seconds: BigInt(Math.floor(reportStartDate.getTime() / 1000)) }
          : undefined,
        to: reportEndDate
          ? { seconds: BigInt(Math.floor(reportEndDate.getTime() / 1000)) }
          : undefined,
      },
      jobStatuses,
    });
    if (!report) return;

    const bytes = toUint8Array(report.content);
    if (!bytes || bytes.length === 0) return;

    // Append the reporting period to the file name, keeping the extension last:
    // "jobs-report.xlsx" -> "jobs-report_2026-07-23_2026-07-23.xlsx".
    const baseName = report.filename?.trim() || "jobs-report.xlsx";
    const rangeSuffix = [reportStartDate, reportEndDate]
      .filter((d): d is Date => d != null)
      .map(toDateParam)
      .join("_");
    const filename = rangeSuffix ? baseName.replace(/(\.[^.]+)?$/, `_${rangeSuffix}$1`) : baseName;
    const blob = new Blob([bytes as unknown as BlobPart], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    downloadBlob(blob, filename);
    setIsReportSheetOpen(false);
  };

  // Download the pickup locations list as an xlsx file. The backend renders the
  // same set PickupLocations returns for pickupLocationsArg (districts, MVO and
  // container-type filters), so the file matches the list on screen. No reporting
  // period here, so the download fires straight from the icon.
  const [triggerExportPickupLocationsReport, { isFetching: isExportingPickupLocationsReport }] =
    useLazyExportPickupLocationsReportQuery();

  const handleExportPickupLocationsReport = async () => {
    if (isExportingPickupLocationsReport) return;
    // A failed request is toasted by GlobalErrorToaster; data is then undefined.
    const { data: report, isError } = await triggerExportPickupLocationsReport(pickupLocationsArg);
    if (isError || !report) return;

    const bytes = toUint8Array(report.content);
    if (!bytes || bytes.length === 0) {
      toast({ title: t(Namespace.Govdash, "pickupLocations.reportEmpty") });
      return;
    }

    const filename = report.filename?.trim() || "pickup-locations-report.xlsx";
    const blob = new Blob([bytes as unknown as BlobPart], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    downloadBlob(blob, filename);
  };

  const { data: trucksData } = useTrucksQuery(govOrganizationQueryArg);
  const { data: depotsData } = useDepotsQuery(govOrganizationQueryArg);
  const { data: disposalSitesData } = useDisposalSitesQuery({});
  const { data: suspiciousLocationsData, refetch: refetchSuspiciousLocations } =
    useSuspiciousLocationsQuery({});
  const { data: anomaliesData } = useAnomaliesListQuery({
    filter: { districtOrganizationIds: [], serviceProviderIds: [] },
    fromTo: {
      from: anomaliesStartDate
        ? { seconds: BigInt(Math.floor(anomaliesStartDate.getTime() / 1000)) }
        : undefined,
      to: anomaliesEndDate
        ? { seconds: BigInt(Math.floor(anomaliesEndDate.getTime() / 1000)) }
        : undefined,
    },
  });

  const [triggerGetGovDisposalVisits, { isFetching: isFetchingGovDisposalVisits }] =
    useLazyGetGovDisposalVisitsQuery();

  // Disposal-site names for the report select. Visits carry only disposalSiteName,
  // so the name is both the option value and its label; dedupe in case two
  // organizations share one.
  const disposalSiteOptions = useMemo(() => {
    const names = ((disposalSitesData?.organizations ?? []) as { name?: string }[])
      .map((org) => org.name?.trim())
      .filter((name): name is string => Boolean(name));
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
  }, [disposalSitesData]);

  // A previously selected site can disappear when the list reloads — fall back to
  // "all" so the select never shows a stale value.
  useEffect(() => {
    if (reportDisposalSite === ALL_DISPOSAL_SITES) return;
    if (!disposalSiteOptions.includes(reportDisposalSite)) {
      setReportDisposalSite(ALL_DISPOSAL_SITES);
    }
  }, [disposalSiteOptions, reportDisposalSite]);

  // Download the disposal-site visits report as an xlsx file. Unlike the jobs
  // report (which the backend renders and returns as bytes), the backend only
  // exposes the raw visit rows here, so the sheet is built on the client from
  // GetGovDisposalVisits over the report sheet's reporting period.
  const handleExportDisposalVisitsReport = async () => {
    if (isFetchingGovDisposalVisits) return;
    try {
      const response = await triggerGetGovDisposalVisits({
        filter: govOrganizationQueryArg,
        fromTo: {
          from: reportStartDate
            ? { seconds: BigInt(Math.floor(reportStartDate.getTime() / 1000)) }
            : undefined,
          to: reportEndDate
            ? { seconds: BigInt(Math.floor(reportEndDate.getTime() / 1000)) }
            : undefined,
        },
      }).unwrap();

      const allVisits = response?.visits ?? [];
      // The backend has no disposal-site filter on this RPC, so narrow the rows
      // client-side when a specific site is selected.
      const visits =
        reportDisposalSite === ALL_DISPOSAL_SITES
          ? allVisits
          : allVisits.filter((visit) => visit.disposalSiteName === reportDisposalSite);
      if (visits.length === 0) {
        window.alert(t(Namespace.Govdash, "disposalSites.visitReport.noData"));
        return;
      }

      const header = [
        t(Namespace.Govdash, "disposalSites.visitReport.organizationName"),
        t(Namespace.Govdash, "disposalSites.visitReport.plateNumber"),
        t(Namespace.Govdash, "disposalSites.visitReport.disposalSiteName"),
        t(Namespace.Govdash, "disposalSites.visitReport.totalVisits"),
        t(Namespace.Govdash, "disposalSites.visitReport.hasScaleData"),
        t(Namespace.Govdash, "disposalSites.visitReport.totalNettoKg"),
      ];
      const yes = t(Namespace.Govdash, "disposalSites.visitReport.yes");
      const no = t(Namespace.Govdash, "disposalSites.visitReport.no");
      const rows: (string | number)[][] = visits.map((visit) => [
        visit.organizationName ?? "",
        visit.plateNumber ?? "",
        visit.disposalSiteName ?? "",
        Number(visit.totalVisits ?? 0),
        visit.hasScaleData ? yes : no,
        visit.hasScaleData ? Number(visit.totalNettoKg ?? 0) : "",
      ]);

      const worksheet = XLSX.utils.aoa_to_sheet([header, ...rows]);
      // Widen the columns so the names/plates are readable without manual resizing.
      worksheet["!cols"] = [
        { wch: 36 },
        { wch: 16 },
        { wch: 32 },
        { wch: 22 },
        { wch: 18 },
        { wch: 18 },
      ];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        // Excel caps sheet names at 31 characters.
        t(Namespace.Govdash, "disposalSites.visitReport.sheetName").slice(0, 31),
      );

      const rangeSuffix = [reportStartDate, reportEndDate]
        .filter((d): d is Date => d != null)
        .map(toDateParam)
        .join("_");
      // Keep the selected site in the filename, stripped down to filesystem-safe
      // characters (the names can contain spaces, quotes and punctuation).
      const siteSuffix =
        reportDisposalSite === ALL_DISPOSAL_SITES
          ? ""
          : reportDisposalSite
              .trim()
              .replace(/[^\p{L}\p{N}]+/gu, "_")
              .replace(/^_+|_+$/g, "")
              .slice(0, 40);
      const fileName = ["disposal_site_visits", siteSuffix, rangeSuffix].filter(Boolean).join("_");
      XLSX.writeFile(workbook, `${fileName}.xlsx`);
      setIsReportSheetOpen(false);
    } catch (error) {
      console.error("Failed to export disposal site visits report", error);
    }
  };

  const listFilterDynamic = useMemo(() => {
    const districtOptions = (govGeoAccessData?.govGeoAccesses ?? []).map((access) => {
      const value = String(access.organizationId);
      const a = access as { areaName?: string; area_name?: string };
      const label =
        (typeof a.areaName === "string" && a.areaName) ||
        (typeof a.area_name === "string" && a.area_name) ||
        value;
      return { value, label };
    });
    const mvoOptions = (serviceProvidersData?.organizations ?? []).map((org) => ({
      value: String(org.id),
      label: org.name || String(org.id),
    }));
    const containerTypeOptions = Array.from(containerTypeIdsByName.keys())
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ value: name, label: name }));
    const trucksDerived = deriveGovTrucksFilterOptions(trucksData?.trucks ?? [], t);
    return {
      districtOptions,
      mvoOptions,
      containerTypeOptions,
      ...trucksDerived,
    };
  }, [govGeoAccessData, serviceProvidersData, containerTypeIdsByName, trucksData, t]);

  const listFilterConfig = useMemo(
    () => getRecordsListFilterConfig(t, recordType, listFilterDynamic, { wasteExportTab }),
    [t, recordType, listFilterDynamic, wasteExportTab],
  );

  useEffect(() => {
    const cfg = getRecordsListFilterConfig(t, recordType, listFilterDynamic, { wasteExportTab });
    if (!cfg) {
      setAppliedExtraFilters([]);
      setIsFilterOpen(false);
      return;
    }
    setAppliedExtraFilters((prev) => {
      const prevByKey = new Map(prev.map((row) => [row.key, row.selectedOptions]));
      return cfg.extraFilters.map((row) => ({
        ...row,
        selectedOptions: (prevByKey.get(row.key) ?? []).filter((value) =>
          row.options.some((opt) => opt.value === value),
        ),
      }));
    });
  }, [recordType, t, listFilterDynamic, wasteExportTab]);

  useEffect(() => {
    setSearchTerm("");
    setViewMode("list");
  }, [recordType]);

  // The AI tab has no search input, so a term typed on the jobs tab must not keep
  // filtering the list after switching.
  useEffect(() => {
    setSearchTerm("");
  }, [wasteExportTab]);

  const showListFilters = Boolean(
    recordType && !selectedRecordId && hasRecordsListFilters(recordType),
  );

  const filteredGovTrucks = useMemo(() => {
    const trucks = trucksData?.trucks ?? [];
    if (recordType !== "trucks") return trucks;
    return applyGovTrucksExtraFilters(trucks, appliedExtraFilters);
  }, [trucksData, recordType, appliedExtraFilters]);

  const trucksDataForList = useMemo(
    () => (trucksData ? { ...trucksData, trucks: filteredGovTrucks } : trucksData),
    [trucksData, filteredGovTrucks],
  );

  const recordsConfig = useMemo(
    (): RecordsConfigItem[] =>
      getRecordsConfig({
        t,
        listFilterDynamic,
        pickupLocationsData,
        jobsData,
        serviceProvidersData,
        trucksData: trucksDataForList,
        depotsData,
        disposalSitesData,
        suspiciousLocationsData,
        anomaliesData,
        aiVerificationsData,
        wasteExportTab,
        selectedRecordId,
      }),
    [
      pickupLocationsData,
      jobsData,
      aiVerificationsData,
      wasteExportTab,
      serviceProvidersData,
      trucksDataForList,
      depotsData,
      disposalSitesData,
      suspiciousLocationsData,
      anomaliesData,
      listFilterDynamic,
      t,
      selectedRecordId,
    ],
  );

  const recordsOfType = useMemo(() => {
    const config = recordsConfig.find((r: RecordsConfigItem) => r.id === recordType);
    if (!config) return [];
    switch (recordType) {
      case "pickup-locations":
        return config.data.map((loc: any) => ({
          id: String(loc.id),
          title: config.cardTitle(loc),
          searchText: loc.name,
          cardRows: config.cardContent(loc),
        }));
      case "waste-export":
        return config.data.map((job: any) => ({
          id: String(job.id),
          title: config.cardTitle(job),
          searchText: job.pickupLocation.name,
          cardRows: config.cardContent(job),
          job: job,
        }));
      case "service-providers":
      case "disposal-sites":
        return config.data.map((org: any) => ({
          id: org.id.toString(),
          title: config.cardTitle(org),
          searchText: org.name,
          cardRows: config.cardContent(org),
        }));
      case "trucks":
        // `truck.truck` is `required` in the proto but optional in the generated
        // type. The lat/lon filter used to keep malformed rows out; without it a
        // single one would throw here and blank the whole page.
        return config.data
          .filter((truck: any) => truck?.truck?.id != null)
          .map((truck: any) => ({
            id: truck.truck.id.toString(),
            title: config.cardTitle(truck),
            pageTitle: config?.pageTitle ? config.pageTitle(truck) : config.cardTitle(truck),
            searchText: `${truck.truck.plateNumber ?? ""} ${truck.truck.model ?? ""}`,
            cardRows: config.cardContent(truck),
          }));
      case "depots":
        return config.data.map((depot: any) => ({
          id: depot.depot.id.toString(),
          title: depot.depot.name,
          searchText: depot.depot.name,
          cardRows: config.cardContent(depot),
        }));
      case "anomalies":
        return config.data.map((anomaly: any) => ({
          id: anomaly.id.toString(),
          title: config.cardTitle(anomaly),
          pageTitle: (
            <div className="border border-text-primary rounded px-2 py-1 font-semibold uppercase text-sm leading-none">
              {anomaly?.truck?.plateNumber}
            </div>
          ),
          searchText: `${anomaly?.truck?.plateNumber ?? ""} ${anomaly?.ownerOrganization?.name ?? ""}`,
          cardRows: config.cardContent(anomaly),
          ...anomaly,
        }));
      case "suspicious-sites":
        return config.data.map((data: any) => ({
          id: data.location.id.toString(),
          title: config.cardTitle(data),
          searchText: data.location.name,
          cardRows: config.cardContent(data),
          ...data,
        }));
      default:
        return [];
    }
  }, [recordType, recordsConfig]);

  const selectedRecord = recordsOfType.find(
    (record: { id: string }) => record.id === selectedRecordId,
  );

  const filteredRecordsOfType = useMemo(() => {
    let records = recordsOfType;
    if (recordType === "suspicious-sites") {
      const selected =
        appliedExtraFilters.find((filter) => filter.key === "archiveState")?.selectedOptions ?? [];
      const showArchived = selected.includes("all") || selected.includes("archived");
      // nothing selected is the default view: active locations only
      const showActive =
        selected.length === 0 || selected.includes("all") || selected.includes("active");
      records = records.filter((record: any) =>
        record.location?.status === ConnectProto.LocationStatus.INACTIVE
          ? showArchived
          : showActive,
      );
    }
    const query = searchTerm.trim().toLowerCase();
    if (!query) return records;

    return records.filter((record: any) => {
      return String(record.searchText ?? "")
        .toLowerCase()
        .includes(query);
    });
  }, [recordsOfType, searchTerm, recordType, appliedExtraFilters]);

  // ── Desktop trucks registry ──
  // The card list is one generic renderer for every record type; trucks get a
  // grouped table instead, but only on desktop where the columns actually fit.
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const showTrucksTable = isDesktop && recordType === "trucks";

  // The table groups by MVO itself, so it takes the raw GovTruckInfo list rather
  // than the flattened card rows. Same lat/lon guard and search as the cards.
  const trucksForTable = useMemo(() => {
    if (!showTrucksTable) return [];
    const query = searchTerm.trim().toLowerCase();
    return filteredGovTrucks.filter((info: any) => {
      if (info?.truck?.id == null) return false;
      if (!info?.lat || !info?.lon) return false;
      if (!query) return true;
      const haystack =
        `${info.truck?.plateNumber ?? ""} ${info.truck?.model ?? ""} ${info.serviceProvider?.name ?? ""}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [showTrucksTable, filteredGovTrucks, searchTerm]);

  const listContainerRef = useRef<HTMLDivElement | null>(null);
  const recordsVirtualizer = useVirtualizer({
    count: filteredRecordsOfType.length,
    getScrollElement: () => listContainerRef.current,
    estimateSize: () => 156,
    overscan: 4,
    gap: 8,
  });

  const virtualItems = recordsVirtualizer.getVirtualItems();
  const lastVisibleIndex = virtualItems.length ? virtualItems[virtualItems.length - 1].index : -1;

  // Asks for the next page once the user has scrolled to the last rendered row.
  // Because rows -> recordsConfig -> recordsOfType -> filteredRecordsOfType is a
  // chain of memos and the virtualizer measures async, filteredRecordsOfType.length
  // can still reflect the previous page when this effect runs; we compare against
  // recordsOfType (the full, pre-search list) so an active search term cannot wedge
  // pagination shut. useAccumulatedList itself guarantees one page per snapshot.
  useEffect(() => {
    if (recordType !== "waste-export") return;
    if (recordsOfType.length < loadedWasteExportCount) return;
    if (lastVisibleIndex >= filteredRecordsOfType.length - 1) {
      loadNextWasteExportPage();
    }
  }, [
    recordType,
    searchTerm,
    loadNextWasteExportPage,
    loadedWasteExportCount,
    lastVisibleIndex,
    filteredRecordsOfType.length,
    recordsOfType.length,
  ]);
  const goBack = () => {
    // If coming from dashboard, return there with restored state
    if ((location.state as any)?.from === "dashboard") {
      const dashboardState: any = {};

      // Restore map state if available
      if ((location.state as any)?.mapState) {
        dashboardState.mapState = (location.state as any).mapState;
      }

      // Restore showTrucks if available
      if ((location.state as any)?.showTrucks) {
        dashboardState.showTrucks = (location.state as any).showTrucks;
      }

      navigate("/dashboard", { state: dashboardState });
      return;
    }

    if (recordType && selectedRecordId) {
      setViewMode("list");
      navigate(`/records/${recordType}${wasteExportTabSearch}`);
      return;
    }

    if (recordType) {
      navigate("/records");
    }
  };

  const getRecordTypeSubtitle = (type?: string) => {
    if (!type || !(type in RECORD_TYPE_TO_SUBTITLE_KEY)) return "";
    const subtitleKey = RECORD_TYPE_TO_SUBTITLE_KEY[type as RecordTypeRoute];
    return t(Namespace.Govdash, subtitleKey as any).toUpperCase();
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col gap-2 overflow-hidden p-2">
      <PageHeader
        recordType={recordType}
        selectedRecord={selectedRecord}
        selectedRecordId={selectedRecordId}
        goBack={goBack}
        getRecordTypeSubtitle={getRecordTypeSubtitle}
        recordConfig={recordsConfig.find((r: RecordsConfigItem) => r.id === recordType)}
        action={
          recordType === "waste-export" && selectedRecordId ? (
            <WasteExportShareButton jobId={selectedRecordId} />
          ) : null
        }
      />

      {/* Tabs for suspicious-sites only */}
      {recordType === "suspicious-sites" && !selectedRecordId && (
        <div className="flex gap-2 rounded-lg bg-brand-blue-light p-1">
          <button
            type="button"
            onClick={() => setViewMode("list")}
            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              viewMode === "list"
                ? "bg-brand-blue text-white"
                : "bg-transparent text-slate-900 hover:bg-white"
            }`}
          >
            {t(Namespace.Govdash, "actions.byList")}
          </button>
          <button
            type="button"
            onClick={() => setViewMode("map")}
            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              viewMode === "map"
                ? "bg-brand-blue text-white"
                : "bg-transparent text-slate-900 hover:bg-white"
            }`}
          >
            {t(Namespace.Govdash, "actions.byMap")}
          </button>
        </div>
      )}

      {recordType === "waste-export" && !selectedRecordId && (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={wasteExportTab === "jobs" ? "solidBlue" : "outline"}
            onClick={() => selectWasteExportTab("jobs")}
          >
            {t(Namespace.Govdash, "records.wasteExport")}
          </Button>
          <Button
            size="sm"
            variant={wasteExportTab === "ai" ? "solidBlue" : "outline"}
            onClick={() => selectWasteExportTab("ai")}
          >
            {t(Namespace.Govdash, "aiVerification.tab")}
          </Button>
        </div>
      )}

      {recordType &&
        !selectedRecordId &&
        (recordType !== "suspicious-sites" || viewMode === "list") && (
          <div className="flex items-center gap-2 justify-between">
            {/* ListAiVerifications takes no search argument, and an input that quietly
                filtered only the already-loaded pages would read as a full search. */}
            {!isAiVerificationList && (
              <div className="flex flex-1 h-8 items-center gap-2 rounded-lg border border-slate-200 bg-white px-2">
                <Search className="size-4 text-slate-500" aria-hidden />
                <input
                  type="text"
                  placeholder={t(Namespace.Common, "search")}
                  className="h-full w-full bg-transparent text-sm font-normal leading-[14px] tracking-tight text-slate-900 placeholder:text-slate-500 focus:outline-none"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            )}
            {recordType === "waste-export" && !selectedRecordId && (
              <div className="flex justify-end">
                <DateRangePicker
                  initialDateFrom={jobsStartDate}
                  initialDateTo={jobsEndDate}
                  onUpdate={(values: any) => {
                    const from = values?.range?.from;
                    const to = values?.range?.to;
                    if (from && to) {
                      const nextFrom = new Date(from);
                      nextFrom.setHours(0, 0, 0, 0);
                      const nextTo = new Date(to);
                      nextTo.setHours(23, 59, 59, 999);
                      setJobsStartDate(nextFrom);
                      setJobsEndDate(nextTo);
                    }
                  }}
                  disableFutureDates={true}
                  showPresets={false}
                />
              </div>
            )}

            {showListFilters && listFilterConfig ? (
              <button
                type="button"
                aria-label={t(Namespace.Govdash, "actions.openFilters" as any)}
                onClick={() => setIsFilterOpen(true)}
                className="relative flex h-8 items-center gap-2 rounded-lg border border-slate-200 bg-white px-2"
              >
                <FilterIcon className="size-4 text-brand-blue" aria-hidden />
                {appliedExtraFilters.filter((f) => f.selectedOptions.length > 0).length > 0 && (
                  <span className="absolute top-[-13px] right-[-8px] text-xs font-medium leading-[14px] tracking-tight bg-brand-blue text-text-invert px-0.5 py-1 rounded-full min-w-[22px] text-center z-10">
                    {appliedExtraFilters.filter((f) => f.selectedOptions.length > 0).length}
                  </span>
                )}
              </button>
            ) : null}
            {recordType === "suspicious-sites" && canEditGovDash && (
              <button
                type="button"
                aria-label={t(Namespace.Govdash, "addNew")}
                onClick={() => setIsAddingLocation(true)}
                className="flex h-8 items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 hover:bg-slate-50"
              >
                <Plus className="size-4 text-brand-blue" aria-hidden />
              </button>
            )}
            {recordType === "pickup-locations" && !selectedRecordId && (
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  aria-label={t(Namespace.Govdash, "pickupLocations.downloadReport")}
                  onClick={handleExportPickupLocationsReport}
                  disabled={isExportingPickupLocationsReport}
                  className="disabled:opacity-50"
                >
                  <IcDownload />
                </button>
              </div>
            )}
            {recordType === "disposal-sites" && !selectedRecordId && (
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  aria-label={t(Namespace.Govdash, "disposalSites.downloadVisitReport")}
                  onClick={() => {
                    // Seed the report period from the current jobs list range.
                    setReportStartDate(jobsStartDate);
                    setReportEndDate(jobsEndDate);
                    setIsReportSheetOpen(true);
                  }}
                  disabled={isExportingJobsReport || isFetchingGovDisposalVisits}
                  className="disabled:opacity-50"
                >
                  <IcDownload />
                </button>
              </div>
            )}
            {recordType === "waste-export" && !selectedRecordId && wasteExportTab === "jobs" && (
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  aria-label={t(Namespace.Govdash, "wasteExport.downloadExportReport")}
                  onClick={() => {
                    // Seed the report period from the current jobs list range.
                    setReportStartDate(jobsStartDate);
                    setReportEndDate(jobsEndDate);
                    setIsReportSheetOpen(true);
                  }}
                  disabled={isExportingJobsReport}
                  className="disabled:opacity-50"
                >
                  <IcDownload />
                </button>
              </div>
            )}
          </div>
        )}

      {recordType === "anomalies" && !selectedRecordId && (
        <div className="flex gap-2 justify-between overflow-hidden">
          <div className="flex justify-end">
            <DateRangePicker
              initialDateFrom={anomaliesStartDate}
              initialDateTo={anomaliesEndDate}
              onUpdate={(values: any) => {
                const from = values?.range?.from;
                const to = values?.range?.to;
                if (from && to) {
                  setAnomaliesStartDate(from);
                  setAnomaliesEndDate(to);
                }
              }}
              disableFutureDates={true}
              showPresets={false}
            />
          </div>
        </div>
      )}

      {isReportSheetOpen && (
        <>
          <div
            className="fixed inset-0 z-[1100] bg-black/40"
            onClick={() => setIsReportSheetOpen(false)}
            aria-hidden
          />
          <div className="fixed overflow-hidden bottom-0 left-0 right-0 z-[1200] w-full bg-white border-t border-slate-200 rounded-t-2xl shadow-[0_4px_20px_-10px_rgba(0,0,0,0.07)] max-h-[70vh] flex flex-col">
            {recordType === "waste-export" && (
              <div className="flex flex-col overflow-y-auto">
                {/* Sticky header */}
                <div className="sticky top-0 bg-white z-20 flex flex-col gap-2 pt-2">
                  <div className="flex items-center justify-between px-4">
                    <p className="font-semibold text-base leading-4 tracking-tight text-text-primary">
                      {t(Namespace.Govdash, "wasteExport.downloadExportReport")}
                    </p>
                    <button
                      type="button"
                      aria-label={t(Namespace.Govdash, "actions.close")}
                      className="w-8 h-8 flex items-center justify-center shrink-0 border-none bg-transparent p-0 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => setIsReportSheetOpen(false)}
                    >
                      <IcCancel className="w-6 h-6 text-gray-900" />
                    </button>
                  </div>
                  <div className="w-full h-px bg-slate-200" />
                </div>

                {/* Content */}
                <div className="flex flex-col gap-4 px-4 py-4 flex-1">
                  <div className="flex gap-2 items-center justify-between">
                    <span className="text-sm font-medium text-slate-900">
                      {t(Namespace.Govdash, "wasteExport.reportingPeriod")}
                    </span>
                    <DateRangePicker
                      initialDateFrom={reportStartDate}
                      initialDateTo={reportEndDate}
                      onUpdate={(values: any) => {
                        const from = values?.range?.from;
                        const to = values?.range?.to;
                        if (from && to) {
                          const nextFrom = new Date(from);
                          nextFrom.setHours(0, 0, 0, 0);
                          const nextTo = new Date(to);
                          nextTo.setHours(23, 59, 59, 999);
                          setReportStartDate(nextFrom);
                          setReportEndDate(nextTo);
                        }
                      }}
                      disableFutureDates={true}
                      showPresets={false}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <Button
                      type="button"
                      disabled={isExportingJobsReport}
                      onClick={() => handleExportJobsReport([ConnectProto.JobStatus.NOT_COMPLETED])}
                    >
                      {t(Namespace.Govdash, "wasteExport.scheduleFailureReport")}
                    </Button>
                    <Button
                      type="button"
                      variant="opacityBlueWithoutOutline"
                      disabled={isExportingJobsReport}
                      onClick={() => handleExportJobsReport([])}
                    >
                      {t(Namespace.Govdash, "wasteExport.fullExportReport")}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {recordType === "disposal-sites" && (
              <div className="flex flex-col overflow-y-auto">
                {/* Sticky header */}
                <div className="sticky top-0 bg-white z-20 flex flex-col gap-2 pt-2">
                  <div className="flex items-center justify-between px-4">
                    <p className="font-semibold text-base leading-4 tracking-tight text-text-primary">
                      {t(Namespace.Govdash, "disposalSites.downloadVisitReport")}
                    </p>
                    <button
                      type="button"
                      aria-label={t(Namespace.Govdash, "actions.close")}
                      className="w-8 h-8 flex items-center justify-center shrink-0 border-none bg-transparent p-0 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => setIsReportSheetOpen(false)}
                    >
                      <IcCancel className="w-6 h-6 text-gray-900" />
                    </button>
                  </div>
                  <div className="w-full h-px bg-slate-200" />
                </div>

                {/* Content */}
                <div className="flex flex-col gap-4 px-4 py-4 flex-1">
                  <div className="flex gap-2 items-center justify-between">
                    <span className="text-sm font-medium text-slate-900">
                      {t(Namespace.Govdash, "wasteExport.reportingPeriod")}
                    </span>
                    <DateRangePicker
                      initialDateFrom={reportStartDate}
                      initialDateTo={reportEndDate}
                      onUpdate={(values: any) => {
                        const from = values?.range?.from;
                        const to = values?.range?.to;
                        if (from && to) {
                          const nextFrom = new Date(from);
                          nextFrom.setHours(0, 0, 0, 0);
                          const nextTo = new Date(to);
                          nextTo.setHours(23, 59, 59, 999);
                          setReportStartDate(nextFrom);
                          setReportEndDate(nextTo);
                        }
                      }}
                      disableFutureDates={true}
                      showPresets={false}
                    />
                  </div>

                  <div className="flex gap-2 items-center justify-between">
                    <span className="text-sm font-medium text-slate-900 shrink-0">
                      {t(Namespace.Govdash, "disposalSites.visitReport.disposalSiteName")}
                    </span>
                    <Select value={reportDisposalSite} onValueChange={setReportDisposalSite}>
                      <SelectTrigger
                        id="disposal-visits-report-site-select"
                        className="w-[200px] border-slate-200"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-[40vh]">
                        <SelectItem value={ALL_DISPOSAL_SITES}>
                          {t(Namespace.Common, "all")}
                        </SelectItem>
                        {disposalSiteOptions.map((name) => (
                          <SelectItem key={name} value={name}>
                            {name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex flex-col gap-2">
                    <Button
                      type="button"
                      disabled={isFetchingGovDisposalVisits}
                      onClick={handleExportDisposalVisitsReport}
                    >
                      {t(Namespace.Common, "download")}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {!recordType ? (
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
          {recordsConfig.map((record: RecordsConfigItem) => (
            <CardContentWrapper key={record.id}>
              <button
                type="button"
                onClick={() => navigate(`/records/${record.id}`)}
                className="flex w-full items-center justify-between p-4"
              >
                <div className="flex gap-2 items-center max-w-[calc(100%-32px)]">
                  <p
                    className="text-base font-semibold leading-4 tracking-tight text-slate-900 truncate"
                    title={t(Namespace.Govdash, record.titleKey as any)}
                  >
                    {t(Namespace.Govdash, record.titleKey as any)}
                  </p>
                  <span className="text-xs font-medium leading-[14px] tracking-tight bg-brand-blue text-text-invert p-1 rounded-2xl min-w-[22px]">
                    {record.totalCount ?? record.data?.length ?? 0}
                  </span>
                </div>
                <div className="flex size-6 items-center justify-center">
                  <div className="flex size-[18px] items-center justify-center rounded-full bg-slate-900 text-white">
                    <ChevronRight className="size-3" strokeWidth={2.5} aria-hidden />
                  </div>
                </div>
              </button>
            </CardContentWrapper>
          ))}
        </div>
      ) : isAddingLocation && recordType === "suspicious-sites" ? (
        <SuspiciousLocationForm
          onBack={() => setIsAddingLocation(false)}
          onSuccess={() => {
            refetchSuspiciousLocations();
          }}
        />
      ) : !selectedRecordId && viewMode === "list" && showTrucksTable ? (
        <div className="flex-1 overflow-auto px-1 pb-1">
          <TrucksTable
            trucks={trucksForTable}
            onTruckClick={(truckId) => navigate(`/records/trucks/${truckId}`)}
          />
        </div>
      ) : !selectedRecordId && viewMode === "list" ? (
        <div ref={listContainerRef} className="flex-1 overflow-y-auto pb-1">
          <div
            className="relative w-full flex"
            style={{
              height: `${recordsVirtualizer.getTotalSize()}px`,
            }}
          >
            {recordsVirtualizer.getVirtualItems().map((virtualRow) => {
              const record = filteredRecordsOfType[virtualRow.index];
              return (
                <div
                  key={record.id}
                  data-index={virtualRow.index}
                  ref={recordsVirtualizer.measureElement}
                  className="absolute left-0 top-0 w-full"
                  style={{
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <CardContentWrapper hasDivider={false}>
                    <div className="flex flex-row">
                      <div className="flex-1 flex flex-col min-w-0">
                        <button
                          type="button"
                          onClick={() =>
                            navigate(`/records/${recordType}/${record.id}${wasteExportTabSearch}`)
                          }
                          className="flex w-full items-center justify-between p-4 border-b"
                        >
                          {typeof record.title === "string" ? (
                            <p className="text-base font-semibold text-start leading-4 tracking-tight text-slate-900 truncate">
                              {record.title}
                            </p>
                          ) : (
                            record.title
                          )}
                          <div className="flex size-6 items-center justify-center shrink-0">
                            <div className="flex size-[18px] items-center justify-center rounded-full bg-slate-900 text-white">
                              <ChevronRight className="size-3" strokeWidth={2.5} aria-hidden />
                            </div>
                          </div>
                        </button>
                        <div className="p-4 flex flex-col gap-2">
                          {record.cardRows?.map((row: any) => (
                            <div
                              key={`${record.id}-${row.label}`}
                              className="flex items-center text-sm font-normal leading-[14px] tracking-tight justify-between gap-2"
                            >
                              <p className={"text-text-secondary flex-1 text-left font-medium"}>
                                {row.label}
                              </p>
                              {isValidElement(row.value) ? (
                                row.value
                              ) : (
                                <p className={"text-text-primary flex-1 text-right"}>{row.value}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                      {recordType === "waste-export" && (
                        <>
                          <WasteExportCardPhotos job={record.job} />
                          {isAiVerificationList && (
                            <AiVerdictBadges t={t} verification={record.job?.aiVerification} />
                          )}
                        </>
                      )}
                    </div>
                  </CardContentWrapper>
                </div>
              );
            })}
            {filteredRecordsOfType.length === 0 && !activeWasteExportList.isFetching && (
              <div className="w-full text-center p-2 text-gray-700">
                {t(Namespace.Common, "noResults")}
              </div>
            )}
            {recordType === "waste-export" && activeWasteExportList.isFetching && (
              <div
                className="absolute left-0 w-full flex justify-center py-3 text-sm text-text-secondary"
                style={{ top: `${recordsVirtualizer.getTotalSize()}px` }}
              >
                {t(Namespace.Common, "loading")}
              </div>
            )}
          </div>
        </div>
      ) : !selectedRecordId && viewMode === "map" ? (
        <div className="flex-1 max-h-[70vh]">
          <RecordsMapView
            records={filteredRecordsOfType}
            recordType={recordType}
            onMarkerClick={(record) => {
              navigate(`/records/${recordType}/${record.id}${wasteExportTabSearch}`);
            }}
          />
        </div>
      ) : selectedRecord || selectedRecordId ? (
        recordType === "anomalies" ? (
          <AnomaliesDetails selectedRecord={selectedRecord} />
        ) : recordType === "suspicious-sites" ? (
          <SuspiciousSitesDetailsComponent selectedRecord={selectedRecord} />
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            {recordsConfig.find((r: RecordsConfigItem) => r.id === recordType)?.content || null}
          </div>
        )
      ) : (
        <CardContentWrapper>
          <InfoRow label={t(Namespace.Settings, "bin")} value="-" />
        </CardContentWrapper>
      )}
      <FilterForm
        open={isFilterOpen}
        onOpenChange={setIsFilterOpen}
        showFilterTabs={false}
        onApply={({ newSelected }) => {
          setAppliedExtraFilters(newSelected);
          setIsFilterOpen(false);
        }}
        extraFilters={listFilterConfig ? appliedExtraFilters : undefined}
      />
    </div>
  );
};

const PageHeader = ({
  recordType,
  selectedRecord,
  goBack,
  getRecordTypeSubtitle,
  recordConfig,
  action,
}: {
  recordType?: string;
  selectedRecord?: any;
  selectedRecordId?: string;
  goBack: () => void;
  getRecordTypeSubtitle: (type?: string) => string;
  recordConfig: any;
  /** An icon button across from the back button. */
  action?: ReactNode;
}) => {
  const t = useT();

  if (!recordType)
    return (
      <div className="flex items-center gap-2 p-2">
        <h1 className="flex flex-1 items-center gap-2 text-[20px] font-bold leading-6 tracking-[-0.1px] text-slate-900">
          {t(Namespace.Govdash, "records.title")}
        </h1>
      </div>
    );

  return (
    <div className="flex justify-center items-center gap-2 h-[4rem] pt-[2px] relative">
      <button
        type="button"
        aria-label={t(Namespace.Govdash, "backToRecords")}
        onClick={goBack}
        className="flex items-center justify-center absolute left-0 size-8 bg-transparent text-gray-900"
      >
        <IcBackTo className="w-4 h-4" />
      </button>
      {action && <div className="absolute right-0">{action}</div>}
      {selectedRecord ? (
        <div className="flex flex-col gap-1 items-center w-full max-w-[calc(100%-80px)]">
          <h1
            className="font-semibold leading-none text-slate-900 truncate text-center w-fit max-w-full"
            title={
              typeof selectedRecord.pageTitle === "string"
                ? selectedRecord.pageTitle
                : typeof selectedRecord.title === "string"
                  ? selectedRecord.title
                  : undefined
            }
          >
            {selectedRecord.pageTitle || selectedRecord.title}
          </h1>
          <p
            className="text-xs text-text-secondary font-bold truncate text-center w-full"
            title={getRecordTypeSubtitle(recordType)}
          >
            {getRecordTypeSubtitle(recordType)}
          </p>
        </div>
      ) : (
        <div className="flex gap-2 flex-1 max-w-[calc(100%-80px)] items-center justify-center font-semibold">
          <p
            className="truncate"
            title={t(Namespace.Govdash, (recordConfig?.titleKey ?? "unknownRecord") as any)}
          >
            {t(Namespace.Govdash, (recordConfig?.titleKey ?? "unknownRecord") as any)}
          </p>
          <span className="text-xs font-medium leading-[14px] tracking-tight bg-brand-blue text-text-invert p-1 rounded-2xl min-w-[22px] text-center">
            {recordConfig?.totalCount ?? recordConfig?.data?.length ?? 0}
          </span>
        </div>
      )}
    </div>
  );
};
